import { MenuItemOption } from "@components/common/mobile/mobile-types";
import { SdbMemberTypes } from "@custom-types/member-types";
import { SdbProject } from "@custom-types/project-types";
import { DeleteIcon } from "@flat-ui";
import { useHasUserValidPermissionProjectLevel } from "@hooks/permission-control/use-has-user-valid-permission-project-level";
import { useHasUserValidRoleCompanyLevel } from "@hooks/access-control/use-has-user-valid-role-company-level";
import { canRemoveMember } from "@components/table/members/members-table-utils";
import { ProjectRemoveMenuButton } from "@components/table/members/project-remove-menu-button";

interface UseProjectRemoveMenuOptionParams {
  /** The company ID */
  companyId: string | undefined;

  /** The selected project */
  selectedProject?: SdbProject | null;
}

interface UseProjectRemoveMenuOptionReturn {
  /** Menu option array to spread into the menu items list */
  projectRemoveMenuOption: MenuItemOption<SdbMemberTypes>[];

  /** Whether the remove action should be shown for the given member */
  shouldShowProjectRemove: (member: SdbMemberTypes) => boolean;
}

/** Provides a reusable "Remove member" menu option for project member tables */
export function useProjectRemoveMenuOption({
  companyId,
  selectedProject,
}: UseProjectRemoveMenuOptionParams): UseProjectRemoveMenuOptionReturn {
  const { canDeleteMemberFromProject } =
    useHasUserValidPermissionProjectLevel({ project: selectedProject });
  const { canDeleteMemberFromCompany } = useHasUserValidRoleCompanyLevel();

  const canRemoveMemberFromProject =
    !!selectedProject &&
    selectedProject.archivingState !== "ARCHIVED" &&
    canDeleteMemberFromProject;

  const hasRequiredIds = !!companyId && !!selectedProject;

  function shouldShowProjectRemove(member: SdbMemberTypes): boolean {
    return canRemoveMember({
      member,
      hasRequiredIds,
      canRemoveFromProject: canRemoveMemberFromProject,
      canDeleteFromCompany: canDeleteMemberFromCompany,
    });
  }

  function getMenuOption(): MenuItemOption<SdbMemberTypes>[] {
    if (!companyId || !selectedProject) {
      return [];
    }

    if (!canRemoveMemberFromProject && !canDeleteMemberFromCompany) {
      return [];
    }

    return [
      {
        label: "Remove member",
        Icon: DeleteIcon,
        component: (member, onMenuClose) => (
          <ProjectRemoveMenuButton
            companyId={companyId}
            project={selectedProject}
            member={member}
            onMenuClose={onMenuClose}
            canRemoveFromWorkspace={canDeleteMemberFromCompany}
          />
        ),
        shouldShow: shouldShowProjectRemove,
      },
    ];
  }

  return {
    projectRemoveMenuOption: getMenuOption(),
    shouldShowProjectRemove,
  };
}
